'use client';

import { useState } from 'react';
import Link from 'next/link';
import { 
  ChevronLeft, 
  Heart, 
  Share2, 
  Printer, 
  Clock, 
  Users, 
  Star,
  ChefHat,
  Lightbulb,
  CheckCircle2,
  Circle
} from 'lucide-react';
import { Button } from '@myapp/ui/components/button';
import { Card, CardContent } from '@myapp/ui/components/card';
import { Badge } from '@myapp/ui/components/badge';
import { Checkbox } from '@myapp/ui/components/checkbox';

// 더미 레시피 데이터
const recipe = {
  id: 1,
  slug: 'gangwon-potato-jeon',
  title: '강원도 감자전',
  description: '평창 감자로 만드는 바삭바삭한 감자전입니다. 집에서 쉽게 만들 수 있어요.',
  image: '🥔',
  difficulty: '쉬움',
  cookTime: '30분',
  prepTime: '15분',
  totalTime: '45분',
  servings: 4,
  rating: 4.8,
  reviewCount: 127,
  tags: ['전통요리', '간단', '제철'],
  author: {
    name: 'AI 셰프',
    avatar: '👨‍🍳'
  },
  ingredients: [
    { id: 1, name: '강원도 감자', amount: '3개', category: '주재료', checked: false },
    { id: 2, name: '양파', amount: '1/2개', category: '주재료', checked: false },
    { id: 3, name: '당근', amount: '1/4개', category: '주재료', checked: false },
    { id: 4, name: '부침가루', amount: '3큰술', category: '반죽', checked: false },
    { id: 5, name: '물', amount: '2큰술', category: '반죽', checked: false },
    { id: 6, name: '계란', amount: '1개', category: '반죽', checked: false },
    { id: 7, name: '소금', amount: '약간', category: '양념', checked: false },
    { id: 8, name: '후추', amount: '약간', category: '양념', checked: false },
    { id: 9, name: '식용유', amount: '적당량', category: '기타', checked: false }
  ],
  steps: [
    {
      id: 1,
      title: '감자 준비하기',
      description: '감자를 깨끗이 씻어서 껍질을 벗기고 강판에 곱게 갈아주세요. 물기를 꽉 짜서 제거합니다.',
      tip: '감자를 간 후 바로 요리하면 색이 변하지 않아요',
      time: '10분'
    },
    {
      id: 2,
      title: '야채 준비하기',
      description: '양파와 당근을 잘게 다져주세요. 크기를 비슷하게 맞춰야 골고루 익습니다.',
      tip: '야채는 너무 크면 전이 부서질 수 있어요',
      time: '5분'
    },
    {
      id: 3,
      title: '반죽 만들기',
      description: '볼에 간 감자, 다진 야채, 부침가루, 계란을 넣고 잘 섞어주세요. 물을 조금씩 넣어가며 농도를 조절합니다.',
      tip: '반죽이 너무 묽으면 부침가루를 더 추가하세요',
      time: '5분'
    },
    {
      id: 4,
      title: '양념하기',
      description: '소금과 후추로 간을 맞춰주세요. 맛을 보면서 조절하는 것이 좋습니다.',
      tip: '간은 살짝 싱겁게 하는 것이 좋아요',
      time: '2분'
    },
    {
      id: 5,
      title: '팬에 굽기',
      description: '팬에 식용유를 두르고 중불로 달궈주세요. 반죽을 한 숟가락씩 떠서 납작하게 펼쳐 굽습니다.',
      tip: '너무 센 불에서 굽면 겉만 타고 속은 설익어요',
      time: '10분'
    },
    {
      id: 6,
      title: '뒤집어 완성하기',
      description: '한쪽 면이 노릇해지면 뒤집어서 반대쪽도 골고루 구워주세요. 양면이 모두 바삭해지면 완성입니다.',
      tip: '뒤집을 때 부서지지 않게 조심하세요',
      time: '8분'
    }
  ],
  nutritionInfo: {
    calories: 180,
    carbs: 28,
    protein: 6,
    fat: 5,
    fiber: 3
  }
};

const relatedRecipes = [
  { id: 1, title: '감자탕', difficulty: '보통', time: '90분', image: '🍲', rating: 4.7 },
  { id: 2, title: '로스트 포테이토', difficulty: '쉬움', time: '45분', image: '🥔', rating: 4.6 },
  { id: 3, title: '감자샐러드', difficulty: '쉬움', time: '25분', image: '🥗', rating: 4.5 },
  { id: 4, title: '감자수프', difficulty: '보통', time: '40분', image: '🍲', rating: 4.8 }
];

const difficultyColors = {
  '쉬움': 'bg-green-100 text-green-700 border-green-200',
  '보통': 'bg-yellow-100 text-yellow-700 border-yellow-200',
  '어려움': 'bg-red-100 text-red-700 border-red-200'
};

export default function RecipeDetailPage({ params }: { params: { slug: string } }) {
  const [checkedIngredients, setCheckedIngredients] = useState<{ [key: number]: boolean }>({});
  const [isBookmarked, setIsBookmarked] = useState(false);

  const toggleIngredient = (ingredientId: number) => {
    setCheckedIngredients(prev => ({
      ...prev,
      [ingredientId]: !prev[ingredientId]
    }));
  };

  const toggleBookmark = () => {
    setIsBookmarked(!isBookmarked);
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="sticky top-16 z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <Link href="/recipes" className="flex items-center text-gray-600 hover:text-gray-900">
              <ChevronLeft className="w-5 h-5 mr-1" />
              <span>레시피 목록</span>
            </Link>
            
            {/* Action Buttons - Desktop */}
            <div className="hidden md:flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={toggleBookmark}
                className={isBookmarked ? 'text-red-500 border-red-200' : ''}
              >
                <Heart className={`w-4 h-4 ${isBookmarked ? 'fill-current' : ''}`} />
              </Button>
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm">
                <Printer className="w-4 h-4" />
                <span className="ml-1">인쇄</span>
              </Button>
            </div>

            {/* Action Buttons - Mobile */}
            <div className="md:hidden flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={toggleBookmark}
                className={isBookmarked ? 'text-red-500 border-red-200' : ''}
              >
                <Heart className={`w-4 h-4 ${isBookmarked ? 'fill-current' : ''}`} />
              </Button>
              <Button variant="outline" size="sm">
                <Share2 className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-6">
        {/* Hero Section */}
        <div className="bg-white rounded-lg shadow-sm overflow-hidden mb-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Recipe Image */}
            <div className="aspect-square lg:aspect-video bg-gradient-to-br from-orange-100 to-yellow-100 flex items-center justify-center text-8xl">
              {recipe.image}
            </div>

            {/* Recipe Info */}
            <div className="p-6 lg:p-8">
              <div className="flex flex-wrap gap-2 mb-4">
                {recipe.tags.map((tag, index) => (
                  <Badge key={index} variant="secondary" className="text-xs">
                    {tag}
                  </Badge>
                ))}
              </div>

              <h1 className="text-3xl font-bold text-gray-900 mb-3">{recipe.title}</h1>
              <p className="text-gray-600 mb-4">{recipe.description}</p>

              <div className="flex items-center gap-4 mb-6">
                <div className="flex items-center">
                  <Star className="w-5 h-5 text-yellow-500 fill-current mr-1" />
                  <span className="font-semibold">{recipe.rating}</span>
                  <span className="text-gray-500 text-sm ml-1">({recipe.reviewCount})</span>
                </div>
                
                <div className="flex items-center text-gray-600">
                  <span className="text-sm">{recipe.author.avatar} {recipe.author.name}</span>
                </div>
              </div>

              {/* Recipe Meta */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                <div className="text-center">
                  <Badge 
                    variant="outline" 
                    className={`mb-2 ${difficultyColors[recipe.difficulty as keyof typeof difficultyColors]}`}
                  >
                    {recipe.difficulty}
                  </Badge>
                  <p className="text-xs text-gray-500">난이도</p>
                </div>
                
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <Clock className="w-4 h-4 mr-1 text-gray-500" />
                    <span className="font-semibold">{recipe.totalTime}</span>
                  </div>
                  <p className="text-xs text-gray-500">총 시간</p>
                </div>
                
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <Users className="w-4 h-4 mr-1 text-gray-500" />
                    <span className="font-semibold">{recipe.servings}인분</span>
                  </div>
                  <p className="text-xs text-gray-500">기준량</p>
                </div>
                
                <div className="text-center">
                  <div className="flex items-center justify-center mb-2">
                    <ChefHat className="w-4 h-4 mr-1 text-gray-500" />
                    <span className="font-semibold">AI</span>
                  </div>
                  <p className="text-xs text-gray-500">레시피</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Ingredients */}
          <div className="lg:col-span-1">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-4">재료 ({recipe.servings}인분)</h2>
                
                <div className="space-y-4">
                  {Object.entries(
                    recipe.ingredients.reduce((acc, ingredient) => {
                      if (!acc[ingredient.category]) acc[ingredient.category] = [];
                      acc[ingredient.category].push(ingredient);
                      return acc;
                    }, {} as { [key: string]: typeof recipe.ingredients })
                  ).map(([category, ingredients]) => (
                    <div key={category}>
                      <h3 className="font-semibold text-gray-800 mb-2">{category}</h3>
                      <div className="space-y-2">
                        {ingredients.map((ingredient) => (
                          <div key={ingredient.id} className="flex items-center space-x-3">
                            <Checkbox
                              id={`ingredient-${ingredient.id}`}
                              checked={checkedIngredients[ingredient.id] || false}
                              onCheckedChange={() => toggleIngredient(ingredient.id)}
                            />
                            <label
                              htmlFor={`ingredient-${ingredient.id}`}
                              className={`flex-1 text-sm cursor-pointer transition-colors ${
                                checkedIngredients[ingredient.id] 
                                  ? 'line-through text-gray-400' 
                                  : 'text-gray-700'
                              }`}
                            >
                              <span className="font-medium">{ingredient.name}</span>
                              <span className="text-gray-500 ml-2">{ingredient.amount}</span>
                            </label>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Cooking Steps */}
          <div className="lg:col-span-2">
            <Card>
              <CardContent className="p-6">
                <h2 className="text-xl font-bold text-gray-900 mb-6">조리 과정</h2>
                
                <div className="space-y-6">
                  {recipe.steps.map((step, index) => (
                    <div key={step.id} className="flex gap-4">
                      {/* Step Number */}
                      <div className="flex-shrink-0">
                        <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center font-bold">
                          {step.id}
                        </div>
                      </div>

                      {/* Step Content */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-gray-900">{step.title}</h3>
                          <Badge variant="outline" className="text-xs">
                            <Clock className="w-3 h-3 mr-1" />
                            {step.time}
                          </Badge>
                        </div>
                        
                        <p className="text-gray-700 mb-3 leading-relaxed">
                          {step.description}
                        </p>
                        
                        {step.tip && (
                          <div className="bg-yellow-50 border-l-4 border-yellow-200 p-3 rounded">
                            <div className="flex items-start gap-2">
                              <Lightbulb className="w-4 h-4 text-yellow-600 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-yellow-800">
                                <span className="font-semibold">팁:</span> {step.tip}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Related Recipes */}
        <div className="mt-12">
          <h2 className="text-2xl font-bold text-gray-900 mb-6">비슷한 레시피</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {relatedRecipes.map((relatedRecipe) => (
              <Card key={relatedRecipe.id} className="cursor-pointer hover:shadow-lg transition-shadow">
                <CardContent className="p-0">
                  <div className="aspect-square bg-gradient-to-br from-orange-100 to-yellow-100 flex items-center justify-center text-6xl">
                    {relatedRecipe.image}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-gray-900 mb-2 truncate">{relatedRecipe.title}</h3>
                    <div className="flex items-center justify-between text-sm text-gray-500 mb-2">
                      <Badge 
                        variant="outline" 
                        className={`text-xs ${difficultyColors[relatedRecipe.difficulty as keyof typeof difficultyColors]}`}
                      >
                        {relatedRecipe.difficulty}
                      </Badge>
                      <span className="flex items-center">
                        <Clock className="w-3 h-3 mr-1" />
                        {relatedRecipe.time}
                      </span>
                    </div>
                    <div className="flex items-center">
                      <Star className="w-4 h-4 text-yellow-500 fill-current mr-1" />
                      <span className="text-sm font-medium">{relatedRecipe.rating}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}